/**
 * Reflection Process Handlers
 *
 * All reflection-related query and mutation handlers
 */

import { handleRequest } from './config'
import { ApiResponse } from '../types'
import { loadCloudFunctionsHandler } from '../handlers'
import type {
  Reflection,
  ReflectionSummary,
  CreateReflectionRequest,
  GenerateReflectionDataRequest,
  UpdateReflectionRequest,
  ArchiveReflectionRequest,
  DeleteReflectionRequest,
  SendReflectionCommitmentsRequest,
  GetReflectionsRequest,
  GetReflectionScopeDataResponse
} from 'pure-js/types/Reflection'
import type {
  CommunicationBalanceReport,
  CommunicativeBalanceSessionReport,
  PsychologicalSafetyReport
} from 'pure-js/types/Report'
import type { State } from '../../hooks/useAppState'

type GenerateReflectionDataResponse = {
  communication_balance_data?: CommunicationBalanceReport
  communication_balance_sessions?: CommunicativeBalanceSessionReport[]
  psych_safety_data?: PsychologicalSafetyReport
}

const emptyState = { user: { id: '' } } as State

// ========================================
// QUERY HANDLERS
// ========================================

export const getReflections = async (
  req: GetReflectionsRequest,
  state?: State
): Promise<ApiResponse<ReflectionSummary[]>> => {
  return handleRequest(
    async (): Promise<ApiResponse<ReflectionSummary[]>> => Promise.resolve({ data: [], success: true }),
    async (): Promise<ApiResponse<ReflectionSummary[]>> => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = state || emptyState
      const result = await cloudFunctions.getReflections(req, stateToUse)
      return { data: result.reflections ?? [], success: result.success, error: result.error }
    }
  )
}

export const getReflection = async (
  reflectionId: string,
  state?: State
): Promise<ApiResponse<Reflection | undefined>> => {
  return handleRequest(
    async (): Promise<ApiResponse<Reflection | undefined>> => Promise.resolve({ data: undefined, success: true }),
    async (): Promise<ApiResponse<Reflection | undefined>> => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = state || emptyState
      const result = await cloudFunctions.getReflection(reflectionId, stateToUse)
      return { data: result.reflection, success: result.success, error: result.error }
    }
  )
}

// ========================================
// MUTATION HANDLERS
// ========================================

export const createReflection = async (
  req: CreateReflectionRequest,
  state?: State
): Promise<ApiResponse<Reflection | undefined>> => {
  return handleRequest(
    async (): Promise<ApiResponse<Reflection | undefined>> => Promise.resolve({ data: undefined, success: true }),
    async (): Promise<ApiResponse<Reflection | undefined>> => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = state || emptyState
      const result = await cloudFunctions.createReflection(req, stateToUse)
      return { data: result.reflection, success: result.success, error: result.error }
    }
  )
}

export const updateReflection = async (
  req: UpdateReflectionRequest,
  state?: State
): Promise<ApiResponse<Reflection | undefined>> => {
  return handleRequest(
    async (): Promise<ApiResponse<Reflection | undefined>> => Promise.resolve({ data: undefined, success: true }),
    async (): Promise<ApiResponse<Reflection | undefined>> => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = state || emptyState
      const result = await cloudFunctions.updateReflection(req, stateToUse)
      return { data: result.reflection, success: result.success, error: result.error }
    }
  )
}

export const deleteReflection = async (
  req: DeleteReflectionRequest,
  state?: State
): Promise<ApiResponse<{ success: boolean }>> => {
  return handleRequest(
    async (): Promise<ApiResponse<{ success: boolean }>> => Promise.resolve({ data: { success: true }, success: true }),
    async (): Promise<ApiResponse<{ success: boolean }>> => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = state || emptyState
      const result = await cloudFunctions.deleteReflection(req, stateToUse)
      return { data: { success: result.success }, success: result.success, error: result.error }
    }
  )
}

export const archiveReflection = async (
  req: ArchiveReflectionRequest,
  state?: State
): Promise<ApiResponse<Reflection | undefined>> => {
  return handleRequest(
    async (): Promise<ApiResponse<Reflection | undefined>> => Promise.resolve({ data: undefined, success: true }),
    async (): Promise<ApiResponse<Reflection | undefined>> => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = state || emptyState
      const result = await cloudFunctions.archiveReflection(req, stateToUse)
      return { data: result.reflection, success: result.success, error: result.error }
    }
  )
}

export const generateReflectionData = async (
  req: GenerateReflectionDataRequest,
  state?: State
): Promise<ApiResponse<GenerateReflectionDataResponse>> => {
  return handleRequest(
    async (): Promise<ApiResponse<GenerateReflectionDataResponse>> => Promise.resolve({ data: {}, success: true }),
    async (): Promise<ApiResponse<GenerateReflectionDataResponse>> => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = state || emptyState
      const result = await cloudFunctions.generateReflectionData(req, stateToUse)
      return {
        data: {
          communication_balance_data: result.communication_balance_data,
          communication_balance_sessions: result.communication_balance_sessions,
          psych_safety_data: result.psych_safety_data
        },
        success: result.success,
        error: result.error
      }
    }
  )
}

export const getReflectionScopeData = async (state?: State): Promise<ApiResponse<GetReflectionScopeDataResponse>> => {
  return handleRequest(
    async (): Promise<ApiResponse<GetReflectionScopeDataResponse>> => Promise.resolve({ data: [], success: true }),
    async (): Promise<ApiResponse<GetReflectionScopeDataResponse>> => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = state || emptyState
      const result = await cloudFunctions.getReflectionScopeData({}, stateToUse)
      return { data: result.data ?? [], success: result.success, error: result.error }
    }
  )
}

export const sendReflectionCommitments = async (
  req: SendReflectionCommitmentsRequest,
  state?: State
): Promise<ApiResponse<{ success: boolean; recipientCount?: number }>> => {
  return handleRequest(
    async (): Promise<ApiResponse<{ success: boolean; recipientCount?: number }>> =>
      Promise.resolve({ data: { success: true, recipientCount: 0 }, success: true }),
    async (): Promise<ApiResponse<{ success: boolean; recipientCount?: number }>> => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = state || emptyState
      const result = await cloudFunctions.sendReflectionCommitments(req, stateToUse)
      return {
        data: { success: result.success, recipientCount: result.recipientCount },
        success: result.success,
        error: result.error
      }
    }
  )
}
